import React, { useMemo, useRef, useLayoutEffect, useState } from 'react';
import { LineChart, PieChart } from '@mui/x-charts';
import { ReportData, FileAttachment } from '../home/components/reportComponent/reportTypes';
import { getRiskLevelColor, getRiskLevelText, getCoverPhotoId, getMapImage, getSatelliteMapImage, isAfterMeasuresValue } from '../home/components/reportComponent/reportUtils';
import { parseFormattedText, renderInlineMarkdownToHtml } from '../reports/[id]/FormattedLlmText';

// ─── Event type colours (same as PinChartModal) ───────────────────────────────
const eventTypeColors: Record<string, string> = {
    'Terörizm (TE)': '#D76E3B',
    'Örgütlü Suçlar (OS)': '#F35E5E',
    'Sosyal ve Politik İstikrarsızlık (SP)': '#67C086',
    'Çevresel ve Doğal Afetler (CA)': '#689D7B',
    'Bilgi Güvenliği ve Enformasyon Tehditleri (BG)': '#63ABC3',
    'Bireysel ve Adi Suçlar (BA)': '#4995DB',
    'Göç ve Mülteci Krizleri (GM)': '#8e6c58',
    'Sağlık ve Biyolojik Tehditler (SB)': '#265454',
    'Yasal ve Düzenleyici Uyumsuzluklar (YD)': '#5C7A9A',
    'Ekonomik ve Finansal Tehditler (EF)': '#9B7A6B',
};

interface HtmlReportTemplateProps {
    reportData: ReportData;
    attachments: { [key: string]: string };
    userData: { firstName: string; lastName: string } | null;
    onPaginationReady?: (isReady: boolean) => void;
    /** Kayıtlı / otomatik üretilen standart (OSM) harita PNG (data URL veya ek ile birleşir). */
    leafletMapStandardUrl?: string | null;
    /** Uydu (Esri World Imagery) PNG — otomatik üretim veya kayıtlı ek yoksa. */
    leafletMapSatelliteUrl?: string | null;
    /** Görünmez Leaflet yakalama bitti mi (yükleniyor / sembolik fallback ayrımı). */
    offscreenMapCaptureDone?: boolean;
    /** Kayıtlı harita dosyası eksikse Leaflet ile yeniden üretim (yalnızca ekran; yazdırmada gizlenir). */
    onRetryLeafletMaps?: () => void;
    // Chart data – passed from ReportView (read from localStorage)
    eventsData?: any;
    chartStartDate?: Date;
    chartEndDate?: Date;
    selectedEventTypes?: string[];
    /** Bulgu / öneri / değerlendirme ek görselleri — satırda yan yana en fazla kaç görsel (sayfalama satırı ile hizalı). */
    attachmentGridColumns?: 2 | 3 | 4;
    /** Ek görsellerin dikey boyutu: küçük = daha fazla sığar; geniş = daha okunaklı. */
    attachmentImageScale?: 'compact' | 'normal' | 'spacious';
}

// ─── Layout constants ─────────────────────────────────────────────────────────
const PAD_X = 50;
const PAD_Y = 16;
const HEADER_H = 68;
const FOOTER_H = 40;
let RUNTIME_CONTENT_H = 855;

// Chart render width: page content area minus padding
const CHART_W = 650;

/** Rapor icin sembolik tesis haritasi (Leaflet degil; PDF/print icin guvenilir). */
function parsePinMapCoords(pin: ReportData['pinInfo']): { lat: number; lng: number; radiusM: number } | null {
    const lat = Number(pin.Latitude);
    const lng = Number(pin.Longitude);
    const rawR = Number(pin.RadiusInMeters);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    const radiusM = !Number.isNaN(rawR) && rawR > 0 ? rawR : 1000;
    return { lat, lng, radiusM };
}

/**
 * Leaflet haritasi yokken sembolik gorunum. Filtre / gradient / ince SVG text kullanmiyoruz:
 * baskida ve html2canvas ile alt katmanlar bazen duser, sadece mavi arka plan kalabiliyordu.
 */
const StaticFacilityMapSvg: React.FC<{ radiusM: number }> = ({ radiusM }) => {
    const width = 1100;
    const height = 620;
    const cx = width / 2;
    const cy = height / 2;
    const maxR = Math.min(width, height) * 0.36;
    const displayRadius = Math.max(48, Math.min(maxR, maxR * (radiusM / (radiusM + 1200))));
    return (
        <svg
            className="rep-static-map-svg"
            xmlns="http://www.w3.org/2000/svg"
            viewBox={`0 0 ${width} ${height}`}
            width="100%"
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label="Tesis konumu ve yaricap sembolu"
        >
            <rect x="0" y="0" width={width} height={height} fill="#f8fafc" stroke="#94a3b8" strokeWidth="2" />
            <g stroke="#cbd5e1" strokeWidth="1.5" opacity={1}>
                {[90, 180, 270, 360, 450, 540].map(y => (
                    <line key={`h${y}`} x1={0} y1={y} x2={width} y2={y} />
                ))}
                {[140, 280, 420, 560, 700, 840, 980].map(x => (
                    <line key={`v${x}`} x1={x} y1={0} x2={x} y2={height} />
                ))}
            </g>
            <circle
                cx={cx}
                cy={cy}
                r={displayRadius}
                fill="#fecaca"
                fillOpacity={0.55}
                stroke="#b91c1c"
                strokeWidth={4}
            />
            <circle cx={cx} cy={cy} r={12} fill="#0f172a" stroke="#ffffff" strokeWidth={3} />
            <rect x={24} y={height - 104} width={520} height={80} rx={8} fill="#ffffff" stroke="#64748b" strokeWidth={2} />
        </svg>
    );
};

// ─── Chart data processing (mirrors PinChartModal.processEventsData) ──────────
interface WeeklyData { date: Date; count: number; weekLabel: string; }
interface EventTypeData { eventType: string; data: { date: Date; count: number }[]; }
const EMPTY_SELECTED_EVENT_TYPES: string[] = [];

function processChartData(
    eventsData: any,
    selectedEventTypes: string[],
): { weeklyData: WeeklyData[]; eventTypeData: EventTypeData[] } {
    try {
        const now = new Date();
        const rangeStart = new Date(now.getFullYear(), now.getMonth() - 11, 1, 0, 0, 0, 0);
        const rangeEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);

        const allEvents = eventsData.locations
            .filter((loc: any) => loc.district1 !== 'Unknown District')
            .flatMap((loc: any) => loc.events);

        let filtered = allEvents;
        if (selectedEventTypes.length > 0) {
            filtered = allEvents.filter((e: any) => selectedEventTypes.includes(e.eventType));
        }
        filtered = filtered.filter((e: any) => {
            const d = new Date(e.date);
            return d >= rangeStart && d <= rangeEnd;
        });

        const monthlyBuckets: WeeklyData[] = Array.from({ length: 12 }, (_, idx) => {
            const monthStart = new Date(rangeStart.getFullYear(), rangeStart.getMonth() + idx, 1, 0, 0, 0, 0);
            return {
                date: monthStart,
                count: 0,
                weekLabel: monthStart.toLocaleDateString('tr-TR', { month: 'short', year: 'numeric' }),
            };
        });

        filtered.forEach((e: any) => {
            const ed = new Date(e.date);
            const monthKey = new Date(ed.getFullYear(), ed.getMonth(), 1, 0, 0, 0, 0).getTime();
            const bucket = monthlyBuckets.find(b => b.date.getTime() === monthKey);
            if (bucket) bucket.count += 1;
        });

        // Event type data (for pie chart) over the same 12-month period
        const eventTypes = Array.from(new Set(filtered.map((e: any) => e.eventType as string)));
        const eventTypeDataArr: EventTypeData[] = eventTypes.map(et => ({
            eventType: String(et),
            data: monthlyBuckets.map(d => ({
                date: d.date,
                count: filtered.filter((e: any) => {
                    const ed = new Date(e.date);
                    return e.eventType === et
                        && ed >= d.date
                        && ed < new Date(d.date.getFullYear(), d.date.getMonth() + 1, 1, 0, 0, 0, 0);
                }).length,
            })),
        }));

        return { weeklyData: monthlyBuckets, eventTypeData: eventTypeDataArr };
    } catch (err) {
        console.warn('Chart data processing failed:', err);
        return { weeklyData: [], eventTypeData: [] };
    }
}

// ─── Block type ────────────────────────────────────────────────────────────────
interface Block {
    node: React.ReactNode;
    isHeading: boolean;
    forceBreakAfter?: boolean;
}
const EVALUATION_TITLE_NORMALIZED = 'sonuc ve degerlendirme';

const normalizeForComparison = (value?: string | null): string =>
    (value ?? '')
        .toLocaleLowerCase('tr-TR')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();

const isEvaluationAnswerEntry = (qa: any): boolean => {
    const category = normalizeForComparison(qa?.answer?.questionCategoryName);
    const questionText = normalizeForComparison(qa?.answer?.questionText);
    return category === EVALUATION_TITLE_NORMALIZED || questionText === EVALUATION_TITLE_NORMALIZED;
};

function paginate(blocks: Block[], heights: number[], maxH: number): Block[][] {
    const pages: Block[][] = [];
    let curPage: Block[] = [];
    let curPageH: number[] = [];
    let curH = 0;
    const safeMax = maxH * 0.90;
    /** Ölçüm gecikmesi / yüklenemeyen img: bloğu sayfadan düşürmeyelim (yaklaşık bir satır yüksekliği). */
    const fallbackBlockH = Math.min(280, Math.max(80, maxH * 0.22));

    for (let i = 0; i < blocks.length; i++) {
        const measured = heights[i];
        const h = measured > 0 ? measured : fallbackBlockH;
        if (h > safeMax) {
            if (curPage.length > 0) { pages.push(curPage); curPage = []; curPageH = []; curH = 0; }
            pages.push([blocks[i]]);
            continue;
        }
        if (curH + h > safeMax && curPage.length > 0) {
            const lastBlock = curPage[curPage.length - 1];
            if (lastBlock.isHeading) {
                const orphanH = curPageH.pop()!;
                curPage.pop();
                curH -= orphanH;
                if (curPage.length > 0) pages.push(curPage);
                curPage = [lastBlock, blocks[i]];
                curPageH = [orphanH, h];
                curH = orphanH + h;
            } else {
                pages.push(curPage);
                curPage = [blocks[i]]; curPageH = [h]; curH = h;
            }
        } else {
            curPage.push(blocks[i]); curPageH.push(h); curH += h;
        }

        if (blocks[i].forceBreakAfter && curPage.length > 0) {
            pages.push(curPage);
            curPage = []; curPageH = []; curH = 0;
        }
    }
    if (curPage.length > 0) pages.push(curPage);
    return pages;
}

// ─── Sub-components ───────────────────────────────────────────────────────────
const PageHeader: React.FC = () => (
    <div className="report-page-header">
        <img src="/tav_logo_pdf_header.png" alt="TAV"
            style={{ height: 50, width: 'auto', opacity: 0.7, objectFit: 'contain' }} />
        <img src="/iso_logo_pdf_header.png" alt="ISO"
            style={{ height: 30, width: 'auto', opacity: 0.7, objectFit: 'contain' }} />
    </div>
);

const PageFooter: React.FC<{ pageNum: number; total: number }> = ({ pageNum, total }) => (
    <div className="report-page-footer">
        <span>TAV-GR-2025/07-01 | GF-6.4.3.</span>
        <span>{pageNum} / {total}</span>
    </div>
);

const CoverPage: React.FC<{
    reportData: ReportData;
    attachments: { [key: string]: string };
}> = ({ reportData, attachments }) => {
    const coverPhotoId = getCoverPhotoId(reportData, attachments);
    return (
        <div className="report-cover-page" data-export-page="true">
            <img src="/background.jpg" alt="" className="report-cover-bg" />
            <div className="report-cover-inner">
                <div className="report-cover-logo-row">
                    <img src="/tav_logo_white.png" alt="TAV"
                        style={{ width: 110, height: 70, objectFit: 'contain' }} />
                </div>
                <div className="report-cover-body">
                    {coverPhotoId && attachments[coverPhotoId] && (
                        <div className="report-cover-photo-frame">
                            <img src={attachments[coverPhotoId]} alt="Kapak" className="report-cover-photo" />
                        </div>
                    )}
                    <div className="report-cover-title">
                        <span>TESİS RİSK VE TEHDİT</span>
                        <span>DEĞERLENDİRME</span>
                        <span>RAPORU</span>
                    </div>
                    <div className="report-cover-date">
                        {new Date().toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' }).toUpperCase()}
                    </div>
                    <div className="report-cover-pin-name">{reportData.pinInfo.Name}</div>
                </div>
                <div className="report-cover-footer">
                    <span>TAV Özel Güvenlik Hizmetleri A.Ş.</span>
                    <span>Strateji Geliştirme ve Kalite Kontrol Koordinatörlüğü</span>
                </div>
            </div>
        </div>
    );
};

const ATTACHMENT_IMAGE_MAX_H: Record<NonNullable<HtmlReportTemplateProps['attachmentImageScale']>, number> = {
    compact: 180,
    normal: 240,
    spacious: 300,
};

/** Ek görselleri sayfa kırılımına uygun satırlara böler (örn. 3 sütun → satır başına en fazla 3 görsel). */
function chunkRepImageItems(
    files: FileAttachment[],
    attachments: Record<string, string>,
    alt: string,
    perRow: number,
): { key: string; src: string; alt: string }[][] {
    const n = Math.max(1, Math.min(4, perRow));
    const withSrc = files.map(f => ({ key: f.id, src: attachments[f.id], alt }));
    if (withSrc.length === 0) return [];
    if (withSrc.length === 1) return [withSrc];
    const rows: { key: string; src: string; alt: string }[][] = [];
    for (let i = 0; i < withSrc.length; i += n) {
        rows.push(withSrc.slice(i, i + n));
    }
    return rows;
}

/** Bulgu / değerlendirme ek görselleri: 1 adet ortada; çoklu satırda sütun sayısı şablondan gelir. */
const RepImageGrid: React.FC<{
    items: { key: string; src: string; alt: string }[];
    maxRowColumns: number;
    maxHeightPx: number;
}> = ({ items, maxRowColumns, maxHeightPx }) => {
    if (items.length === 0) return null;
    const layout = items.length === 1 ? 'rep-img-grid rep-img-grid--single' : 'rep-img-grid rep-img-grid--multi';
    const colCount = Math.min(items.length, Math.max(1, Math.min(4, maxRowColumns)));
    const gridStyle = {
        '--rep-cols': colCount,
        '--rep-img-max-h': `${maxHeightPx}px`,
    } as React.CSSProperties;
    return (
        <div className={layout} data-count={items.length} style={gridStyle}>
            {items.map(it => (
                <img key={it.key} src={it.src} alt={it.alt} className="rep-img-grid__img" />
            ))}
        </div>
    );
};

// ─── Block helpers ─────────────────────────────────────────────────────────────
let _blockKey = 0;
const bk = () => `b${_blockKey++}`;
function B(el: React.ReactNode, isHeading = false, forceBreakAfter = false): Block {
    return { node: <div className="rep-block-wrap">{el}</div>, isHeading, forceBreakAfter };
}

function buildFormattedReportTextBlocks(text: string): Block[] {
    const parsed = parseFormattedText(text ?? '');
    if (parsed.length === 0) {
        return [B(<p key={bk()} className="rep-p">İçerik bulunamadı.</p>)];
    }

    const result: Block[] = [];
    parsed.forEach((block) => {
        if (block.type === 'heading') {
            result.push(
                B(
                    <p
                        key={bk()}
                        className="rep-sub-title"
                        style={{ marginTop: 6, marginBottom: 6 }}
                        dangerouslySetInnerHTML={{ __html: renderInlineMarkdownToHtml(block.text) }}
                    />,
                    true,
                ),
            );
            return;
        }

        if (block.type === 'paragraph') {
            result.push(B(
                <p
                    key={bk()}
                    className="rep-p"
                    dangerouslySetInnerHTML={{ __html: renderInlineMarkdownToHtml(block.text) }}
                />,
            ));
            return;
        }

        const isOrdered = block.type === 'ordered-list';
        result.push(B(
            React.createElement(
                isOrdered ? 'ol' : 'ul',
                {
                    key: bk(),
                    className: 'rep-p',
                    style: { marginTop: 4, marginBottom: 8, paddingLeft: 22 },
                },
                block.items.map((item, idx) => (
                    <li
                        key={idx}
                        style={{ marginBottom: 4 }}
                        dangerouslySetInnerHTML={{ __html: renderInlineMarkdownToHtml(item) }}
                    />
                )),
            ),
        ));
    });

    return result;
}

function stripIdsFromNode(node: React.ReactNode): React.ReactNode {
    if (!React.isValidElement(node)) return node;

    const element = node as React.ReactElement<any>;
    const nextProps: Record<string, unknown> = {};

    if (element.props?.id !== undefined) {
        nextProps.id = undefined;
    }

    if (element.props?.children !== undefined) {
        nextProps.children = React.Children.map(element.props.children, child => stripIdsFromNode(child));
    }

    return React.cloneElement(element, nextProps);
}

// ─── Main template ─────────────────────────────────────────────────────────────
const HtmlReportTemplate: React.FC<HtmlReportTemplateProps> = ({
    reportData, attachments, userData,
    onPaginationReady,
    leafletMapStandardUrl = null,
    leafletMapSatelliteUrl = null,
    offscreenMapCaptureDone = true,
    onRetryLeafletMaps,
    eventsData, chartStartDate, chartEndDate, selectedEventTypes,
    attachmentGridColumns = 3,
    attachmentImageScale = 'normal',
}) => {
    const attachCols = Math.max(2, Math.min(4, attachmentGridColumns));
    const attachMaxH = ATTACHMENT_IMAGE_MAX_H[attachmentImageScale];
    const anchorScrollOffsetStyle = {
        scrollMarginTop: 'calc(var(--report-sticky-offset, 0px) + 64px)',
    } as const;
    _blockKey = 0;
    const selectedTypes = selectedEventTypes ?? EMPTY_SELECTED_EVENT_TYPES;

    const measureRef = useRef<HTMLDivElement>(null);
    const pageRef = useRef<HTMLDivElement>(null);
    const [pages, setPages] = useState<Block[][]>([]);
    const [ready, setReady] = useState(false);

    // ── Section groupings ────────────────────────────────────────────────────
    const evaluationAnswer = useMemo(() =>
        reportData.questionAnswers.find(qa => isEvaluationAnswerEntry(qa)),
    [reportData]);

    const groupedQuestions = useMemo(() =>
        reportData.questionAnswers
            .filter(qa => !isEvaluationAnswerEntry(qa))
            .reduce((acc, qa) => {
                const cat = qa.answer.questionCategoryName;
                if (cat) { if (!acc[cat]) acc[cat] = []; acc[cat].push(qa.answer); }
                return acc;
            }, {} as Record<string, any[]>),
        [reportData]);

    const groupedRiskAnswers = useMemo(() =>
        (reportData.riskAnswers ?? []).reduce((acc, ra) => {
            if (!acc[ra.categoryName]) acc[ra.categoryName] = [];
            acc[ra.categoryName].push(ra);
            return acc;
        }, {} as Record<string, any[]>),
        [reportData]);

    const mapImageId = useMemo(() => getMapImage(reportData), [reportData]);
    const satelliteMapImageId = useMemo(() => getSatelliteMapImage(reportData), [reportData]);
    const pinMapCoords = useMemo(() => parsePinMapCoords(reportData.pinInfo), [reportData.pinInfo]);

    const orderedCategories = useMemo(() => Array.from(
        new Set([...Object.keys(groupedQuestions), ...Object.keys(groupedRiskAnswers)])
    ).filter(cat => {
        const qCount = (groupedQuestions[cat] ?? []).filter(a => a.findings?.trim() || a.proposedSolutions?.trim()).length;
        const rCount = (groupedRiskAnswers[cat] ?? []).length;
        return qCount > 0 || rCount > 0;
    }), [groupedQuestions, groupedRiskAnswers]);

    // ── Chart data (processed here, no capture needed) ───────────────────────
    const { weeklyData, eventTypeData } = useMemo(() => {
        if (!eventsData) return { weeklyData: [], eventTypeData: [] };
        return processChartData(eventsData, selectedTypes);
    }, [eventsData, selectedTypes]);

    // ── Build flat block list ────────────────────────────────────────────────
    const blocks = useMemo<Block[]>(() => {
        const result: Block[] = [];

        // ── İçindekiler ──
        const tocItems = [
            { label: "RAPOR DETAYLARI", id: "rapor-detaylari" },
            { label: "GENEL BAKIŞ", id: "genel-bakis" },
            { label: "FONKSİYONEL KONUM VE ERİŞİM AĞI", id: "fonksiyonel-konum" },
            { label: "RİSK ANALİZİ METODOLOJİSİ", id: "risk-analizi-metodolojisi" },
            { label: "RİSK DEĞERLENDİRME SKALASI", id: "risk-degerlendirme-skalasi" }
        ];
        
        orderedCategories.forEach((category, idx) => {
            tocItems.push({ label: category.toLocaleUpperCase('tr-TR'), id: `cat-${idx}` });
        });
        
        if (evaluationAnswer) {
            tocItems.push({ label: "SONUÇ VE DEĞERLENDİRME", id: "sonuc-ve-degerlendirme" });
        }
        const tocContent = (
            <div className="rep-toc-container">
                <h2 className="rep-section-title">İÇİNDEKİLER</h2>
                {tocItems.map((item, idx) => (
                    <p key={idx} className="rep-p" style={{ marginBottom: 6 }}>
                        <a href={`#${item.id}`} style={{ textDecoration: 'none', color: '#003087', fontWeight: 600 }}>
                            {idx + 1}. {item.label}
                        </a>
                    </p>
                ))}
            </div>
        );
        result.push(B(tocContent, false, true));

        // ── Rapor Detayları ──
        result.push(B(<h2 id="rapor-detaylari" key={bk()} className="rep-section-title" style={anchorScrollOffsetStyle}>RAPOR DETAYLARI</h2>, true));
        result.push(B(<p key={bk()} className="rep-p"><strong>Ait Olduğu Dönem / Tarih:</strong><br />{new Date().toLocaleDateString('tr-TR')}</p>));
        result.push(B(<p key={bk()} className="rep-p"><strong>Düzenleyen Kurum / Kişi:</strong><br />TAV Özel Güvenlik Hizmetleri A.Ş.{userData ? ` / ${userData.firstName} ${userData.lastName}` : ''}</p>));
        result.push(B(<p key={bk()} className="rep-p"><strong>Risk ve Tehdit Analizi Yapılan Kurum:</strong><br />{reportData.pinInfo.Name}</p>));
        result.push(B(<p key={bk()} className="rep-p"><strong>Değerlendirme Yaklaşımı:</strong><br />Risk ve Tehdit Değerlendirme Raporu; Kabiliyet, Maksat, Zafiyet, Kalıntı Risk kavramları dikkate alınarak ve Uzaktan Merkeze Değerlendirme Yöntemi ile hazırlanmıştır.</p>));
        result.push(B(<p key={bk()} className="rep-p"><strong>Raporun Amacı:</strong></p>));
        result.push(B(<p key={bk()} className="rep-bullet">• Risk ve tehdit unsurlarını belirlemek,</p>));
        result.push(B(<p key={bk()} className="rep-bullet">• Bu unsurların değerlendirmesini yaparak risk ve tehdit seviyesini tespit etmek,</p>));
        result.push(B(<p key={bk()} className="rep-bullet">• Tespit edilmiş risk ve tehdit seviyesine istinaden alınması gereken etkin karşı tedbirleri belirlemektir.</p>));

        // ── Genel Bakış ──
        result.push(B(<h2 id="genel-bakis" key={bk()} className="rep-section-title" style={{ marginTop: 24, ...anchorScrollOffsetStyle }}>GENEL BAKIŞ</h2>, true));
        result.push(
            ...buildFormattedReportTextBlocks(
                reportData.pinInfo.reportDescription || 'Genel bakış bilgisi bulunmamaktadır.',
            ),
        );

        // ── Fonksiyonel Konum ve Erişim Ağı (Genel Bakış hemen ardından) ──
        const effectiveStandardMapSrc =
            (mapImageId && attachments[mapImageId]) || leafletMapStandardUrl || null;
        const effectiveSatelliteMapSrc =
            (satelliteMapImageId && attachments[satelliteMapImageId]) || leafletMapSatelliteUrl || null;
        const showMapSection =
            !!pinMapCoords || !!effectiveStandardMapSrc || !!effectiveSatelliteMapSrc;
        const showSymbolicMapFallback =
            !!pinMapCoords &&
            offscreenMapCaptureDone &&
            !effectiveStandardMapSrc &&
            !effectiveSatelliteMapSrc;

        if (showMapSection) {
            /* Tek pagination birimi: başlık ile haritalar simüle sayfada asla ayrılmaz (rep-block-wrap yüksekliği birlikte ölçülür). */
            const bundleK = bk();
            const bundleParts: React.ReactNode[] = [
                <h2 id="fonksiyonel-konum" key={`${bundleK}-h`} className="rep-section-title" style={{ marginTop: 24, ...anchorScrollOffsetStyle }}>
                    FONKSİYONEL KONUM VE ERİŞİM AĞI
                </h2>,
            ];

            if (effectiveStandardMapSrc) {
                bundleParts.push(
                    <p key={`${bundleK}-st`} className="rep-sub-title">Standart harita görünümü</p>,
                );
                bundleParts.push(
                    <img
                        key={`${bundleK}-si`}
                        src={effectiveStandardMapSrc}
                        alt="Standart harita görünümü"
                        className="rep-img rep-map-figure"
                    />,
                );
            } else if (mapImageId && pinMapCoords && offscreenMapCaptureDone) {
                bundleParts.push(
                    <p key={`${bundleK}-sf`} className="rep-p" style={{ color: '#92400e' }}>
                        Kayıtlı standart harita dosyası sunucuda bulunamadı veya indirilemedi (dosya silinmiş olabilir).
                    </p>,
                );
            } else if (pinMapCoords && !offscreenMapCaptureDone) {
                bundleParts.push(
                    <p key={`${bundleK}-sl`} className="rep-p" style={{ fontStyle: 'italic', color: '#64748b' }}>
                        Standart harita görüntüsü oluşturuluyor…
                    </p>,
                );
            } else if (showSymbolicMapFallback) {
                bundleParts.push(
                    <div key={`${bundleK}-sym`} className="rep-static-map-block">
                        <div className="rep-static-map-wrap">
                            <StaticFacilityMapSvg radiusM={pinMapCoords!.radiusM} />
                        </div>
                        <p className="rep-static-map-coords">
                            <strong>Tesis konumu:</strong>{' '}
                            Enlem {pinMapCoords!.lat.toFixed(6)}, Boylam {pinMapCoords!.lng.toFixed(6)}
                            {' · '}
                            <strong>Yarıçap:</strong> {Math.round(pinMapCoords!.radiusM)} m
                        </p>
                        <p className="rep-static-map-note">
                            Harita karoları yüklenemedi; kayıtlı dosya yokken sembolik gösterim kullanılır.
                        </p>
                    </div>,
                );
            }

            if (effectiveSatelliteMapSrc) {
                bundleParts.push(
                    <p key={`${bundleK}-ut`} className="rep-sub-title" style={{ marginTop: 14 }}>Uydu görünümü</p>,
                );
                bundleParts.push(
                    <img
                        key={`${bundleK}-ui`}
                        src={effectiveSatelliteMapSrc}
                        alt="Uydu haritası"
                        className="rep-img rep-map-figure"
                    />,
                );
            } else if (satelliteMapImageId && pinMapCoords && offscreenMapCaptureDone) {
                bundleParts.push(
                    <p key={`${bundleK}-uf`} className="rep-p" style={{ marginTop: 8, color: '#92400e' }}>
                        Kayıtlı uydu dosyası sunucuda bulunamadı veya indirilemedi (dosya silinmiş olabilir).
                    </p>,
                );
            } else if (pinMapCoords && !offscreenMapCaptureDone) {
                bundleParts.push(
                    <p key={`${bundleK}-uv`} className="rep-p" style={{ fontStyle: 'italic', color: '#64748b', marginTop: 8 }}>
                        Uydu görüntüsü oluşturuluyor…
                    </p>,
                );
            }

            const staleStandardRef =
                !!mapImageId && !!pinMapCoords && offscreenMapCaptureDone && !effectiveStandardMapSrc;
            const staleSatelliteRef =
                !!satelliteMapImageId &&
                !!pinMapCoords &&
                offscreenMapCaptureDone &&
                !effectiveSatelliteMapSrc;
            if ((staleStandardRef || staleSatelliteRef) && onRetryLeafletMaps) {
                bundleParts.push(
                    <div key={`${bundleK}-retry`} className="no-print" style={{ marginTop: 6 }}>
                        <button type="button" className="rep-map-retry-btn" onClick={onRetryLeafletMaps}>
                            Haritaları yeniden oluştur (eksik dosyaları tekrar üret)
                        </button>
                    </div>,
                );
            }

            const rRaw = Number(reportData.pinInfo.RadiusInMeters);
            const radiusLabel = pinMapCoords?.radiusM ?? (!Number.isNaN(rRaw) && rRaw > 0 ? rRaw : 1000);
            if (pinMapCoords || effectiveStandardMapSrc || effectiveSatelliteMapSrc) {
                bundleParts.push(
                    <p key={`${bundleK}-r`} className="rep-p" style={{ textAlign: 'center', fontSize: 11, color: '#666' }}>
                        Seçilen yarıçap: {radiusLabel} m.
                    </p>,
                );
            }

            result.push(B(
                <div key={bundleK} className="rep-fonksiyonel-map-bundle">
                    {bundleParts}
                </div>,
                false,
            ));
        }

        // ── Line Chart (direkt SVG embed) ──
        if (weeklyData.length > 0) {
            result.push(B(<p key={bk()} className="rep-sub-title">Aylık Olay Sayısı (Son 12 Ay)</p>, true));
            result.push(B(
                <div key={bk()} style={{ width: '100%' }}>
                    <LineChart
                        skipAnimation
                        xAxis={[{
                            data: weeklyData.map(d => d.weekLabel),
                            scaleType: 'band',
                            tickLabelStyle: { angle: 30, textAnchor: 'start', fontSize: 10 },
                        }]}
                        yAxis={[{ min: 0 }]}
                        series={[{
                            data: weeklyData.map(d => d.count),
                            curve: 'linear',
                            showMark: true,
                            color: '#094151',
                            valueFormatter: (v) => v ? `Olay Sayısı: ${v}` : null,
                        }]}
                        width={CHART_W}
                        height={240}
                        margin={{ left: 40, right: 20, top: 16, bottom: 70 }}
                        sx={{
                            '.MuiLineElement-root': { strokeWidth: 2 },
                            '.MuiMarkElement-root': { strokeWidth: 2, r: 4, stroke: '#fff', fill: 'currentColor' },
                            /* Make static and prevent tooltips/hover scaling that break PDF generation */
                            pointerEvents: 'none',
                        }}
                        tooltip={{ trigger: 'none' }} // Disable interactive tooltip
                        disableAxisListener // Disable axis hover interactions
                        slotProps={{ legend: { hidden: true } }}
                    />
                </div>
            ));
        }

        // ── Pie Chart (direkt SVG embed) ──
        if (eventTypeData.length > 0) {
            const pieData = eventTypeData
                .map((td, idx) => {
                    const count = td.data.reduce((s, d) => s + d.count, 0);
                    return {
                        id: td.eventType,
                        value: count,
                        label: `${td.eventType} (${count})`,
                        color: eventTypeColors[td.eventType] || `hsl(${idx * (360 / Math.max(eventTypeData.length, 1))}, 70%, 50%)`,
                    };
                })
                .filter(item => item.value > 0);

            if (pieData.length > 0) {
            result.push(B(<p key={bk()} className="rep-sub-title">Olay Tiplerine Göre Dağılım</p>, true));
            result.push(B(
                <div key={bk()} style={{ width: '100%' }}>
                    <PieChart
                        skipAnimation
                        series={[{
                            data: pieData,
                            highlightScope: { faded: 'none', highlighted: 'none' }, // Remove hover scaling completely
                            arcLabel: (item) => `${item.value}`,
                        }]}
                        width={CHART_W}
                        height={240}
                        margin={{ right: 200 }}
                        sx={{ pointerEvents: 'none' }} // Ensure no mouse hits are accepted to show popups
                        tooltip={{ trigger: 'none' }} // Ensure tooltips are bypassed
                        slotProps={{
                            legend: {
                                direction: 'column',
                                position: { vertical: 'middle', horizontal: 'right' },
                                padding: { left: 0, right: 10, top: 0, bottom: 0 },
                                itemMarkWidth: 8, itemMarkHeight: 8,
                                markGap: 4, itemGap: 6,
                                labelStyle: { fontSize: 10 },
                            },
                        }}
                    />
                </div>
            ));
            }
        }

        // ── Risk Analizi Metodolojisi ──
        result.push(B(<h2 id="risk-analizi-metodolojisi" key={bk()} className="rep-section-title" style={{ marginTop: 24, ...anchorScrollOffsetStyle }}>RİSK ANALİZİ METODOLOJİSİ</h2>, true));
        result.push(B(<p key={bk()} className="rep-p">Bu rapor, güvenlik risklerini oluşturan ana bileşenler arasındaki nedensel ilişki zincirini aşağıdaki formüller ile modellemektedir.</p>));
        result.push(B(
            <table key={bk()} className="rep-table">
                <thead><tr><th>Formül</th><th>Açıklama</th><th>İlgili Fonksiyon</th></tr></thead>
                <tbody>
                    <tr><td>Kabiliyet + Maksat → Tehdit</td><td>Aktörün eylem kapasitesi ile niyetinin birleşimi</td><td>İstihbarat</td></tr>
                    <tr><td>Tehdit + Zafiyet → Risk</td><td>Tehditlerin mevcut güvenlik açıklarıyla etkileşimi</td><td>Operasyon &amp; Araştırma</td></tr>
                    <tr><td>Risk – Tedbirler → Kalıntı Risk</td><td>Alınan önlemler sonrası geriye kalan risk seviyesi</td><td>Önleyici Güvenlik Planlaması</td></tr>
                </tbody>
            </table>
        ));
        [
            ['Maksat', 'Eylem gerçekleştirme niyeti.'],
            ['Kabiliyet', 'Bu niyetin fiiliyata dönüştürülebilmesini kolaylaştıran imkân ve kapasite.'],
            ['Tehdit', 'Maksat ve kabiliyetin birleşiminden doğan potansiyel tehlike.'],
            ['Risk', 'Bu tehdidin, mevcut zafiyetlerle etkileşime girmesi sonucu oluşan zarar ihtimali.'],
        ].forEach(([term, desc]) => {
            result.push(B(<p key={bk()} className="rep-p"><strong style={{ color: '#003087' }}>{term}:</strong> {desc}</p>));
        });

        result.push(B(<h2 id="risk-degerlendirme-skalasi" key={bk()} className="rep-section-title" style={{ marginTop: 24, ...anchorScrollOffsetStyle }}>RİSK DEĞERLENDİRME SKALASI</h2>, true));
        result.push(B(<p key={bk()} className="rep-p">Risk seviyesi, nitelikli saha gözlemleri, yapay zekâ destekli veri analizi ve uzman değerlendirmeleri doğrultusunda sınıflandırılmıştır.</p>));
        result.push(B(
            <table key={bk()} className="rep-table" style={{ width: '60%' }}>
                <thead><tr><th>Kalıntı Risk (r)</th><th>Alarm Seviyesi</th></tr></thead>
                <tbody>
                    <tr><td>r &lt; 3</td><td style={{ backgroundColor: '#e0e0e0', fontWeight: 600 }}>DÜŞÜK</td></tr>
                    <tr><td>3 ≤ r ≤ 10</td><td style={{ backgroundColor: '#fff176', fontWeight: 600 }}>ORTA</td></tr>
                    <tr><td>10 &lt; r &lt; 20</td><td style={{ backgroundColor: '#ffb74d', fontWeight: 600 }}>YÜKSEK</td></tr>
                    <tr><td>r ≥ 20</td><td style={{ backgroundColor: '#ef5350', color: 'white', fontWeight: 600 }}>KRİTİK</td></tr>
                </tbody>
            </table>
        ));

        // ── Category sections ──
        orderedCategories.forEach(category => {
            const qAnswers = (groupedQuestions[category] ?? []).filter(a => a.findings?.trim() || a.proposedSolutions?.trim());
            const rAnswers = groupedRiskAnswers[category] ?? [];

            result.push(B(<h2 id={`cat-${orderedCategories.indexOf(category)}`} key={bk()} className="rep-section-title" style={{ marginTop: 24, ...anchorScrollOffsetStyle }}>{category.toLocaleUpperCase('tr-TR')}</h2>, true));

            qAnswers.forEach((ans: any, idx: number) => {
                const allQaFiles = (ans.fileAttachments ?? []).filter((f: FileAttachment) => attachments[f.id]);
                const findingsImgFiles = allQaFiles.filter(
                    (f: FileAttachment) => f.attachmentSection == null || f.attachmentSection === 1,
                );
                const proposedImgFiles = allQaFiles.filter((f: FileAttachment) => f.attachmentSection === 2);

                if (ans.findings?.trim()) result.push(B(<p key={bk()} className="rep-p"><strong>{idx + 1}. Bulgu: </strong>{ans.findings}</p>));
                chunkRepImageItems(findingsImgFiles, attachments, 'Bulgu ek görsel', attachCols).forEach(items => {
                    result.push(B(<RepImageGrid key={bk()} items={items} maxRowColumns={attachCols} maxHeightPx={attachMaxH} />));
                });

                if (ans.proposedSolutions?.trim()) result.push(B(<p key={bk()} className="rep-p"><strong>{idx + 1}. Öneri: </strong>{ans.proposedSolutions}</p>));
                chunkRepImageItems(proposedImgFiles, attachments, 'Öneri ek görsel', attachCols).forEach(items => {
                    result.push(B(<RepImageGrid key={bk()} items={items} maxRowColumns={attachCols} maxHeightPx={attachMaxH} />));
                });
            });

            if (rAnswers.length > 0) {
                result.push(B(<p key={bk()} className="rep-sub-title">RİSK TABLOSU</p>, true));
                (['Öncesi', 'Sonrası'] as const).forEach(k => {
                    const isAfter = k === 'Sonrası';
                    const row = rAnswers.find((r: any) => isAfterMeasuresValue(r.isAfterMeasures) === isAfter);
                    if (!row) return;
                    const color = getRiskLevelColor(row.residualRiskLevel);
                    result.push(B(
                        <div key={bk()}>
                            <p className="rep-p" style={{ fontWeight: 600, marginBottom: 4 }}>Etkin Karşı Tedbirler {k} Risk ve Tehdit Seviyesi</p>
                            <table className="rep-risk-table">
                                <thead>
                                    <tr><th>Olasılık</th><th>Etki</th><th>Tehdit</th><th>Tedbirler Kat Sayısı</th><th>Kalıntı Risk</th><th>Risk Seviyesi</th></tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{row.probabilityLevel}</td>
                                        <td>{row.impactLevel}</td>
                                        <td>{row.threatLevel}</td>
                                        <td>{row.appliedMeasureCoefficient}</td>
                                        <td>{row.residualRiskLevel}</td>
                                        <td style={{ backgroundColor: color.backgroundColor, color: color.color, fontWeight: 700 }}>{getRiskLevelText(row.residualRiskLevel)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    ));
                });
            }
        });

        // ── Sonuç ve Değerlendirme ──
        if (evaluationAnswer) {
            result.push(B(<h2 id="sonuc-ve-degerlendirme" key={bk()} className="rep-section-title" style={{ marginTop: 24, ...anchorScrollOffsetStyle }}>SONUÇ VE DEĞERLENDİRME</h2>, true));
            const descText = evaluationAnswer.answer.description ?? '';
            result.push(...buildFormattedReportTextBlocks(descText));

            const evalImgFiles = (evaluationAnswer.answer.fileAttachments ?? [])
                .filter((f: FileAttachment) => attachments[f.id]);
            chunkRepImageItems(evalImgFiles, attachments, 'Değerlendirme ek', attachCols).forEach(items => {
                result.push(B(<RepImageGrid key={bk()} items={items} maxRowColumns={attachCols} maxHeightPx={attachMaxH} />));
            });
        }

        return result;
    }, [reportData, attachments, userData, leafletMapStandardUrl, leafletMapSatelliteUrl,
        offscreenMapCaptureDone, orderedCategories, evaluationAnswer,
        groupedQuestions, groupedRiskAnswers, mapImageId, pinMapCoords, weeklyData, eventTypeData,
        attachCols, attachMaxH]);

    // ── Two-pass pagination ────────────────────────────────────────────────────
    useLayoutEffect(() => {
        if (!measureRef.current || blocks.length === 0) return;
        setReady(false);
        onPaginationReady?.(false);
        const container = measureRef.current;
        let cancelled = false;
        let finished = false;
        let retryTimer: number | null = null;
        let fallbackTimer: number | null = null;
        let hardTimeout: number | null = null;
        let onLoad: (() => void) | null = null;
        const MAX_ATTEMPTS = 120; // ~6s with 50ms retry interval
        const RETRY_MS = 50;

        const finishWithFallback = () => {
            if (cancelled || finished) return;
            finished = true;
            if (hardTimeout !== null) {
                window.clearTimeout(hardTimeout);
                hardTimeout = null;
            }
            setPages([blocks]);
            setReady(true);
            onPaginationReady?.(true);
        };

        const run = (attempt = 0) => {
            if (cancelled || finished) return;
            const children = Array.from(container.children) as HTMLElement[];
            if (children.length !== blocks.length) {
                if (attempt < MAX_ATTEMPTS) {
                    retryTimer = window.setTimeout(() => run(attempt + 1), RETRY_MS);
                    return;
                }
                // Avoid permanent loading state if hidden measurement children never settle.
                finishWithFallback();
                return;
            }

            const liveContent = pageRef.current?.querySelector('.report-page-content') as HTMLElement | null
                ?? document.querySelector('.report-page-content') as HTMLElement | null;
            if (liveContent) RUNTIME_CONTENT_H = liveContent.clientHeight - 4;

            const heights = children.map(el => el.getBoundingClientRect().height);
            const paginated = paginate(blocks, heights, RUNTIME_CONTENT_H);
            finished = true;
            if (hardTimeout !== null) {
                window.clearTimeout(hardTimeout);
                hardTimeout = null;
            }
            setPages(paginated);
            setReady(true);
            onPaginationReady?.(true);
        };

        const imgs = Array.from(container.querySelectorAll('img'));
        const pending = imgs.filter(img => !img.complete || img.naturalHeight === 0);

        if (pending.length === 0) {
            retryTimer = window.setTimeout(() => run(), 0);
        } else {
            let loaded = 0;
            onLoad = () => {
                if (++loaded === pending.length) {
                    retryTimer = window.setTimeout(() => run(), 0);
                }
            };
            pending.forEach(img => {
                img.addEventListener('load', onLoad);
                img.addEventListener('error', onLoad);
            });
            fallbackTimer = window.setTimeout(() => {
                run(MAX_ATTEMPTS);
            }, 5000);
        }

        // Hard safety-net: never keep spinner forever.
        hardTimeout = window.setTimeout(() => {
            finishWithFallback();
        }, 8000);

        return () => {
            cancelled = true;
            if (retryTimer !== null) window.clearTimeout(retryTimer);
            if (fallbackTimer !== null) window.clearTimeout(fallbackTimer);
            if (hardTimeout !== null) window.clearTimeout(hardTimeout);
            pending.forEach(img => {
                if (!onLoad) return;
                img.removeEventListener('load', onLoad);
                img.removeEventListener('error', onLoad);
            });
        };
    }, [blocks, onPaginationReady]);

    useLayoutEffect(() => {
        return () => onPaginationReady?.(false);
    }, [onPaginationReady]);

    return (
        <div className="report-outer">
            {/* Cover */}
            <CoverPage reportData={reportData} attachments={attachments} />

            {/* Hidden measurement container – same CSS class as actual page content for identical font/width context */}
            <div
                ref={measureRef}
                aria-hidden="true"
                className="report-page-content"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: '-9999px',
                    width: '210mm',
                    height: 'auto',
                    maxHeight: 'none',
                    overflow: 'visible',
                    visibility: 'hidden',
                    pointerEvents: 'none',
                    zIndex: -1,
                }}
            >
                {blocks.map((block, i) => (
                    <div key={i}>{stripIdsFromNode(block.node)}</div>
                ))}
            </div>

            {/* Paginated content */}
            {ready ? (
                pages.map((pageBlocks, i) => (
                    <div key={i} ref={i === 0 ? pageRef : undefined} className="report-a4-page" data-export-page="true">
                        <PageHeader />
                        <div className="report-page-content">
                            {pageBlocks.map((block, j) => (
                                <React.Fragment key={j}>{block.node}</React.Fragment>
                            ))}
                        </div>
                        <PageFooter pageNum={i + 1} total={pages.length} />
                    </div>
                ))
            ) : (
                <div ref={pageRef} className="report-a4-page">
                    <PageHeader />
                    <div className="report-page-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
                        <div style={{ width: 40, height: 40, border: '4px solid #E2E8F0', borderTopColor: '#003087', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                        <p style={{ color: '#64748B', fontSize: 14, margin: 0 }}>Sayfa düzeni hesaplanıyor…</p>
                        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                    </div>
                    <PageFooter pageNum={1} total={1} />
                </div>
            )}
        </div>
    );
};

export default HtmlReportTemplate;
